<template>
  <div class="crm-view">
    <div class="container container--fluid">
      <PageHeader :title="$t('crm.title')" :description="$t('crm.description')"></PageHeader>
      
      <div class="crm-view__controls">
        <TableView
          :leads="leads || []"
          :loading="loading"
          :total="pagination?.total || 0"
          :onCreateLead="openCreateLeadModal"
          :search-query="searchQuery"
          @row-click="handleLeadClick"
          @search="handleSearch"
          @page-change="handlePageChange"
        />
      </div>

      <!-- Lead Detail Drawer -->
      <LeadDetailDrawer
        :show="showLeadDrawer"
        :lead-id="selectedLeadId"
        @close="closeLeadDrawer"
        @lead-updated="handleLeadUpdated"
        @lead-archived="closeLeadDrawer"
        @lead-deleted="closeLeadDrawer"
      />
    </div>
  </div>
</template>

<script>
  import { ref, computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { useCrmStore } from '@/store/crmStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useI18n } from '@/composables/useI18n';

  import LeadDetails from '@/components/crm/LeadDetails.vue';
  import LeadDetailDrawer from '@/components/crm/LeadDetailDrawer.vue';
  import CreateEditLead from '@/components/crm/CreateEditLead.vue';
  import Tabs from '@/components/partials/Tabs/Tabs.vue';
  import Tab from '@/components/partials/Tabs/Tab.vue';
  import TableView from '@/components/crm/TableView.vue';
  import KanbanView from '@/components/crm/KanbanView.vue';
  import PageHeader from '@/components/partials/PageHeader.vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  
  export default {
    components: {
      Tabs,
      Tab,
      TableView,
      KanbanView,
      PageHeader,
      LeadDetailDrawer,
      Btn,
      TextInput,
    },
    setup() {
      const crmStore = useCrmStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const router = useRouter();
      const { t } = useI18n();

      const viewMode = ref('table');
      const loading = computed(() => crmStore.loading);

      // Search
      const searchQuery = ref('');
      const searchTimeout = ref(null);

      // Drawer state
      const showLeadDrawer = ref(false);
      const selectedLeadId = ref(null);

      const leads = computed(() => crmStore.leads);
      const pagination = computed(() => crmStore.pagination);
      const kanbanStatuses = [
        { value: 'new', label: t('crm.statuses.new') },
        { value: 'contacted', label: t('crm.statuses.contacted') },
        { value: 'introduction', label: t('crm.statuses.introduction') },
        { value: 'qualified', label: t('crm.statuses.qualified') },
        { value: 'converted', label: t('crm.statuses.converted') },
        { value: 'lost', label: t('crm.statuses.lost') },
      ];

      // Update groupedLeads computed to use the store's leads
      const groupedLeads = computed(() => {
        const grouped = {};
        kanbanStatuses.forEach((status) => {
          grouped[status.value] =
            status.value === 'new'
              ? leads.value.filter((lead) => !lead.status || lead.status === 'new')
              : leads.value.filter((lead) => lead.status === status.value);
        });
        return grouped;
      });

      const handleStatusChange = async (lead, newStatus, originalStatus) => {
        const leadToUpdate = leads.value.find((l) => l.id === lead.id);
        if (leadToUpdate) {
          leadToUpdate.status = newStatus;
        }

        try {
          await crmStore.updateLead(lead.id, { status: newStatus });
          toastStore.addToast({
            message: t('crm.lead_status_updated'),
            type: 'success',
            duration: 3000,
          });
        } catch (error) {
          console.error('Failed to update status:', error);
          if (leadToUpdate) {
            leadToUpdate.status = originalStatus;
          }
        }
      };

      const handleLeadClick = (lead, event) => {
        // Check if it's a mobile device or small screen
        const isMobile = window.innerWidth <= 768;

        // Check if Ctrl/Cmd key is pressed for new tab
        const openInNewTab = event?.ctrlKey || event?.metaKey;

        if (isMobile || openInNewTab) {
          // Navigate to standalone page
          if (openInNewTab) {
            window.open(router.resolve(`/crm/lead/${lead.id}`).href, '_blank');
          } else {
            router.push(`/crm/lead/${lead.id}`);
          }
        } else {
          // Open in drawer
          selectedLeadId.value = lead.id;
          showLeadDrawer.value = true;
        }
      };

      const closeLeadDrawer = () => {
        showLeadDrawer.value = false;
        selectedLeadId.value = null;
        // Clear current lead after a delay to allow for smooth closing animation
        setTimeout(() => {
          crmStore.clearCurrentLead();
        }, 300);
      };

      const handleLeadUpdated = (updatedLead) => {
        // Lead is already updated in the store by the LeadDetailContent component
        // We could add additional logic here if needed
        toastStore.addToast({
          message: t('crm.lead_updated'),
          type: 'success',
          duration: 3000,
        });
      };

      // Legacy modal function for backwards compatibility
      const openLeadModal = (lead) => {
        uiStore.addModal(LeadDetails, {
          title: t('crm.lead_details'),
          leadId: lead.id,
          size: 'xxl',
        });
      };

      // Open create lead modal
      const openCreateLeadModal = () => {
        const modalId = uiStore.addModal(CreateEditLead, {
          title: t('crm.create_lead.title'),
          description: t('crm.create_lead.description'),
          size: 'lg',
          hasComponent: true,
          onCreated: (newLead) => {
            // Optionally open the lead drawer after creation
            selectedLeadId.value = newLead.id;
            showLeadDrawer.value = true;
          },
          onClose: () => {
            uiStore.closeModal(modalId);
          },
        });
      };

      const currentPageSize = ref(50);

      const loadLeads = async (page = 1, limit = currentPageSize.value) => {
        const params = {
          page,
          limit,
        };

        if (searchQuery.value.trim()) {
          params.search = searchQuery.value.trim();
        }

        await crmStore.fetchLeads(params);
      };

      const handlePageChange = ({ page, limit }) => {
        currentPageSize.value = limit;
        loadLeads(page, limit);
      };

      const handleSearch = (value) => {
        searchQuery.value = value;
        if (searchTimeout.value) {
          clearTimeout(searchTimeout.value);
        }
        searchTimeout.value = setTimeout(() => {
          loadLeads(1, currentPageSize.value);
        }, 300);
      };

      onMounted(() => {
        loadLeads(1, currentPageSize.value);
      });

      return {
        viewMode,
        leads,
        loading,
        kanbanStatuses,
        groupedLeads,
        handleStatusChange,
        handleLeadClick,
        openLeadModal,
        openCreateLeadModal,
        pagination,
        handlePageChange,
        searchQuery,
        handleSearch,
        // Drawer state
        showLeadDrawer,
        selectedLeadId,
        closeLeadDrawer,
        handleLeadUpdated,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/_mixins.scss';
  
  .crm-view {
    &__controls {
      margin-bottom: 20px;
    }
    
    &__kanban-search {
      padding: 0.5rem 1rem;
      margin-bottom: 1rem;
      
      &-input {
        width: 100%;
      }
    }
  }
  
  .submission-view {
    &__controls {
      margin-bottom: 20px;
    }
    &__chart {
      min-height: 25vh;
      margin-bottom: 2rem;
      @include card('sm');
    }
  }
</style>
